
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MaintenanceAlertsList from './MaintenanceAlertsList';
import MaintenanceParametersList from './MaintenanceParametersList';
import AiPredictionsList from './AiPredictionsList';
import { maintenanceAlerts, maintenanceParameters, aiPredictions } from './data/maintenanceData';
import { Device } from '@/components/device-health/gis-map/types';
import MaintenanceSummaryBar from './MaintenanceSummaryBar';

interface MaintenanceTabsProps {
  defaultTab?: string;
  onTabChange?: (tab: string) => void;
  selectedDeviceId?: string;
  deviceData?: Device | null;
}

const MaintenanceTabs = ({ 
  defaultTab = "alerts", 
  onTabChange,
  selectedDeviceId,
  deviceData
}: MaintenanceTabsProps) => {
  const [selectedTab, setSelectedTab] = React.useState(defaultTab);
  const [assetTypeFilter, setAssetTypeFilter] = React.useState<string | null>(null);
  const [priorityFilter, setPriorityFilter] = React.useState<string | null>(null);
  const [facilityFilter, setFacilityFilter] = React.useState<string | null>(null);
  
  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    if (onTabChange) {
      onTabChange(value);
    }
  };

  // Filter alerts by device ID if specified
  const filteredAlerts = selectedDeviceId 
    ? maintenanceAlerts.filter(alert => 
        alert.deviceId === selectedDeviceId || 
        // Include critical alerts regardless of device
        alert.status === 'Alert'
      )
    : maintenanceAlerts;

  const handleFilterChange = (filterType: 'assetType' | 'priority' | 'facility', value: string | null) => {
    switch (filterType) {
      case 'assetType':
        setAssetTypeFilter(value);
        break;
      case 'priority':
        setPriorityFilter(value);
        break;
      case 'facility':
        setFacilityFilter(value);
        break;
    }
  };

  // Get count of alerts by priority
  const alertCounts = {
    total: filteredAlerts.length,
    critical: filteredAlerts.filter(alert => alert.status === 'Alert').length,
    medium: filteredAlerts.filter(alert => alert.status === 'Warning').length,
    low: filteredAlerts.filter(alert => alert.status === 'Good').length,
  };

  return (
    <Tabs defaultValue={defaultTab} value={selectedTab} onValueChange={handleTabChange} className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="alerts">System Alerts</TabsTrigger>
        <TabsTrigger value="parameters">Maintenance Parameters</TabsTrigger>
        <TabsTrigger value="ai-predictions">AI Predictions</TabsTrigger>
      </TabsList>
      
      <MaintenanceSummaryBar 
        alertCounts={alertCounts}
        onFilterChange={handleFilterChange}
        selectedAssetType={assetTypeFilter}
        selectedPriority={priorityFilter}
        selectedFacility={facilityFilter}
      />
      
      <TabsContent value="alerts" className="mt-0">
        <MaintenanceAlertsList 
          alerts={filteredAlerts} 
          selectedDeviceId={selectedDeviceId}
          deviceData={deviceData}
          assetTypeFilter={assetTypeFilter}
          priorityFilter={priorityFilter}
          facilityFilter={facilityFilter}
        />
      </TabsContent>
      
      <TabsContent value="parameters" className="mt-0">
        <MaintenanceParametersList 
          parameters={maintenanceParameters}
          selectedDeviceId={selectedDeviceId}
          deviceData={deviceData}
          assetTypeFilter={assetTypeFilter}
          facilityFilter={facilityFilter}
        />
      </TabsContent>

      <TabsContent value="ai-predictions" className="mt-0">
        <AiPredictionsList 
          predictions={aiPredictions}
          selectedDeviceId={selectedDeviceId}
          deviceData={deviceData}
          assetTypeFilter={assetTypeFilter}
          priorityFilter={priorityFilter}
          facilityFilter={facilityFilter}
        />
      </TabsContent>
    </Tabs>
  );
};

export default MaintenanceTabs;
